import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  ControlPosition,
  MapControl,
  useMapsLibrary
} from '@vis.gl/react-google-maps'

import { Button, Icon, Input } from '@yes.technology/react-toolkit'

import { MapFilter, PlaceAutocomplete } from './MapContainer.styles'

type CustomAutocompleteControlProps = {
  controlPosition: ControlPosition
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void
}

export const CustomMapControl = ({
  controlPosition,
  onPlaceSelect
}: CustomAutocompleteControlProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)

  const [inputValue, setInputValue] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const places = useMapsLibrary('places')

  const { t } = useTranslation()

  useEffect(() => {
    if (!places || !inputRef.current) return

    const options = {
      types: ['(regions)'],
      componentRestrictions: { country: 'BR' },
      fields: ['geometry', 'name', 'formatted_address', 'address_components']
    }

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options))
  }, [places])

  useEffect(() => {
    if (!placeAutocomplete) return

    setInputValue(placeAutocomplete.getPlace()?.formatted_address || '')

    placeAutocomplete.addListener('place_changed', () => {
      onPlaceSelect(placeAutocomplete.getPlace())
    })
  }, [onPlaceSelect, placeAutocomplete])

  const onClear = () => {
    setInputValue('')
  }

  return (
    <MapControl position={controlPosition}>
      <MapFilter>
        <PlaceAutocomplete>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={setInputValue}
            placeholder={t('map-container.input-placeholder')}
            heightClass='regular'
          />
        </PlaceAutocomplete>
        <Button variant='secondary' onClick={onClear} heightClass='regular'>
          <Icon iconName='Rubber' icSize='semiSmall' />
        </Button>
      </MapFilter>
    </MapControl>
  )
}
